/**
 * The case queues — Mine, Team, All, Requests and Reviews (plan §4, FR-L4). A
 * list since Wave 15C (C14 §10.6 items 1–2, ADR-0033): the kit's table with
 * search, filters, sort and paging, an export of the cases, and a row that
 * opens the case.
 *
 * **The tab strip is the permission model, visible.** `visibleQueues` drops
 * Team and All without `can_view_all`, the same way the sidebar drops a nav
 * entry: an associate's book is their own work, and an empty "Team" tab
 * advertises a capability they cannot obtain. **Requests** is dropped for a
 * role that cannot open a case, which is what accepting one does.
 *
 * One read serves a whole queue. A queue that fetched its service per row is
 * the N+1 that makes a case list feel like the reference system, so the server
 * denormalises `service_name` onto the row; the client names come from one
 * read of the client list beside it.
 *
 * **Search is the list's own**, over what it holds (up to 200 cases). When a
 * queue holds more than that, the search is also sent to the server as `q` —
 * the old screen's search — so it still reaches every case. Server-side paging
 * is not part of C14 (§10.6 item 8).
 */

import { Button, EmptyState, Tabs, formatDate } from '@fiveox/ui'
import { Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import {
  ExportButton,
  type ListColumn,
  type ListFilter,
  ListPage,
  ListTable,
  ListToolbar,
  StatusBadge,
  useListLoading,
  useListTable,
} from '../../components/page'
import { CASE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  type Case,
  type CaseVocabulary,
  type Priority,
  type Queue,
  type ReviewRow,
  type ServiceRequest,
  type StageCategory,
  CATEGORY_LABELS,
  PRIORITY_LABELS,
  QUEUE_LABELS,
  caseFlows,
  caseLabel,
  cases,
  dueLabel,
  statusPair,
  visibleQueues,
} from '../../lib/api/cases'
import { type ClientRecord, clients } from '../../lib/api/clients'
import { NewCaseDialog } from './NewCaseDialog'
import { RequestList } from './Requests'

/** How a due date reads, by `dueLabel`'s tone. Tokens only. */
export const DUE_TONE: Record<string, string> = {
  overdue: 'text-destructive font-medium',
  today: 'text-foreground font-medium',
  soon: 'text-foreground',
  none: 'text-muted-foreground',
}

/** The most a queue read returns (the service caps `limit` at 200). */
const QUEUE_LIMIT = 200

const CATEGORIES: StageCategory[] = ['open', 'in_progress', 'review', 'completed', 'cancelled']
const PRIORITIES: Priority[] = ['urgent', 'high', 'normal', 'low']

const DUE_FILTERS = [
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Due today' },
  { value: 'week', label: 'Due in 7 days' },
  { value: 'none', label: 'No due date' },
]

function matchDue(row: Case, value: string): boolean {
  const due = dueLabel(row.due_date)
  if (value === 'overdue') return due.tone === 'overdue'
  if (value === 'today') return due.tone === 'today'
  if (value === 'week') return due.tone === 'today' || due.tone === 'soon'
  if (value === 'none') return row.due_date === null
  return true
}

function CaseList({
  queue,
  rows,
  clientNames,
  canCreate,
  onCreate,
  onSearch,
}: {
  queue: Queue
  rows: Case[]
  clientNames: Map<string, string>
  canCreate: boolean
  onCreate: () => void
  onSearch: (q: string) => void
}) {
  const loading = useListLoading()
  const wide = queue === 'team' || queue === 'all'
  const client = (row: Case) => clientNames.get(row.client_id) ?? ''

  const columns = useMemo<ListColumn<Case>[]>(
    () => [
      {
        key: 'case',
        label: 'Case',
        width: 240,
        required: true,
        phone: true,
        sortField: 'number',
        render: (row) => (
          <span className="block min-w-0">
            <span className="block max-w-[10.5rem] truncate sm:max-w-none">{caseLabel(row)}</span>
            <span className="text-muted-foreground block text-xs font-normal">{row.number}</span>
          </span>
        ),
        exportValue: (row) => caseLabel(row),
      },
      {
        key: 'number',
        label: 'Number',
        width: 0,
        render: () => null,
        exportValue: (row) => row.number,
      },
      {
        key: 'client',
        label: 'Client',
        width: 180,
        render: (row) => <span className="text-foreground">{client(row) || '—'}</span>,
        exportValue: (row) => client(row),
      },
      {
        key: 'status',
        label: 'Stage',
        width: 170,
        phone: true,
        sortField: 'category',
        render: (row) => {
          const status = statusPair(row)
          return (
            <div className="min-w-0 space-y-0.5">
              <StatusBadge kind="case" status={row.category} label={status.firm} />
              {/* The client's word, only when it differs from the firm's
                  (ADR-0009 §3). Showing both when they are the same teaches a
                  reader the two columns are one thing. */}
              {status.client && (
                <p className="text-muted-foreground hidden text-xs sm:block">Client sees: {status.client}</p>
              )}
            </div>
          )
        },
        exportValue: (row) => row.status,
      },
      {
        key: 'priority',
        label: 'Priority',
        width: 100,
        sortField: 'priority',
        render: (row) => (
          <span
            className={
              row.priority === 'urgent' || row.priority === 'high'
                ? 'text-foreground font-medium'
                : 'text-muted-foreground'
            }
          >
            {PRIORITY_LABELS[row.priority]}
          </span>
        ),
        exportValue: (row) => PRIORITY_LABELS[row.priority],
      },
      {
        key: 'due',
        label: 'Due',
        width: 110,
        sortField: 'due_date',
        render: (row) => {
          const due = dueLabel(row.due_date)
          return <span className={`whitespace-nowrap ${DUE_TONE[due.tone]}`}>{due.text}</span>
        },
        exportValue: (row) => (row.due_date ? formatDate(row.due_date) : ''),
      },
      ...(wide
        ? [
            {
              key: 'assignee',
              label: 'With',
              width: 110,
              // Eight characters of the user id — ProDesk has no member
              // directory to name them from (ADR-0012 §2).
              render: (row: Case) => (
                <span className="text-muted-foreground font-mono text-xs">
                  {row.assignee_user_id ? row.assignee_user_id.slice(0, 8) : 'Nobody yet'}
                </span>
              ),
              exportValue: (row: Case) => row.assignee_user_id ?? '',
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wide, clientNames],
  )

  // The number column exists only for the export; the table never draws it.
  const shown = useMemo(() => columns.filter((c) => c.key !== 'number'), [columns])

  const periods = useMemo(
    () => [...new Set(rows.map((row) => row.period).filter((p): p is string => Boolean(p)))].sort(),
    [rows],
  )

  const filters: ListFilter<Case>[] = [
    {
      key: 'category',
      label: 'Stage',
      allLabel: 'Any stage',
      options: CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABELS[c] })),
      match: (row, value) => row.category === value,
    },
    {
      key: 'priority',
      label: 'Priority',
      allLabel: 'Any priority',
      options: PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABELS[p] })),
      match: (row, value) => row.priority === value,
    },
    {
      key: 'due',
      label: 'Due',
      allLabel: 'Any due date',
      options: DUE_FILTERS,
      match: matchDue,
    },
    ...(wide
      ? [
          {
            key: 'assignee',
            label: 'With',
            allLabel: 'Anybody',
            options: [
              { value: 'assigned', label: 'Assigned' },
              { value: 'unassigned', label: 'Nobody yet' },
            ],
            match: (row: Case, value: string) =>
              value === 'unassigned' ? !row.assignee_user_id : Boolean(row.assignee_user_id),
          },
        ]
      : []),
    ...(periods.length > 1
      ? [
          {
            key: 'period',
            label: 'Period',
            allLabel: 'Every period',
            options: periods.map((p) => ({ value: p, label: p })),
            match: (row: Case, value: string) => row.period === value,
          },
        ]
      : []),
  ]

  const list = useListTable(rows, {
    columns: shown,
    search: (row) => [row.number, caseLabel(row), client(row), row.service_name, row.status, row.period],
    filters,
    initialSort: { field: 'due_date', dir: 'asc' },
  })

  const search = list.toolbar.search ?? ''
  useEffect(() => {
    const timer = setTimeout(() => onSearch(search.trim()), 350)
    return () => clearTimeout(timer)
  }, [search, onSearch])

  if (!loading && list.isEmpty && !search) {
    return (
      <EmptyState
        title={queue === 'mine' ? 'Nothing on your desk' : 'No cases here'}
        description={
          queue === 'mine'
            ? 'Cases assigned to you, or waiting on your review, appear here. A new case is assigned by a partner or a practice manager.'
            : 'Cases open from your compliance calendar, an accepted request or proposal, or by hand.'
        }
        action={
          canCreate ? (
            <Button onClick={onCreate}>
              <Plus size={16} aria-hidden /> Open a case
            </Button>
          ) : undefined
        }
      />
    )
  }

  return (
    <div className="space-y-4">
      <ListToolbar
        list={list}
        placeholder="Search by number, title or client"
        actions={<ExportButton rows={list.sorted} columns={columns} filename={`prodesk-cases-${queue}`} />}
      />
      <ListTable
        list={list}
        rowKey={(row) => row.id}
        rowHref={(row) => `/cases/${row.id}`}
        empty={<span className="text-muted-foreground text-sm">No case matches “{search}”.</span>}
      />
    </div>
  )
}

function ReviewList({ rows }: { rows: ReviewRow[] }) {
  const loading = useListLoading()
  const columns = useMemo<ListColumn<ReviewRow>[]>(
    () => [
      {
        key: 'case',
        label: 'Case',
        width: 240,
        required: true,
        phone: true,
        render: (row) => (
          <span className="block min-w-0">
            <span className="block truncate">{caseLabel(row.case)}</span>
            <span className="text-muted-foreground block text-xs font-normal">
              {row.case.number} · waiting at {row.case.status}
            </span>
          </span>
        ),
      },
      {
        key: 'outcome',
        label: 'What deciding does',
        width: 280,
        // Snapshotted on the review row, so this sentence is one query and not
        // a workflow resolution per line.
        render: (row) => (
          <span className="text-muted-foreground text-sm">
            Approving sends it to <strong className="text-foreground">{row.on_approve ?? '—'}</strong>
            ; sending it back goes to{' '}
            <strong className="text-foreground">{row.on_reject ?? '—'}</strong>.
          </span>
        ),
      },
      {
        key: 'status',
        label: 'Who decides',
        width: 180,
        phone: true,
        render: (row) =>
          row.can_decide ? (
            <StatusBadge kind="case" status="review" label="Yours to decide" />
          ) : (
            <span className="text-muted-foreground text-xs">
              Waiting on {row.reviewer_role ?? 'a reviewer'}
            </span>
          ),
      },
    ],
    [],
  )
  const list = useListTable(rows, {
    columns,
    search: (row) => [row.case.number, caseLabel(row.case), row.case.status, row.reviewer_role],
    filters: [
      {
        key: 'decider',
        label: 'Who decides',
        allLabel: 'Anybody',
        options: [
          { value: 'me', label: 'Yours to decide' },
          { value: 'other', label: 'Somebody else' },
        ],
        match: (row, value) => (value === 'me' ? row.can_decide : !row.can_decide),
      },
    ],
  })

  if (!loading && list.isEmpty) {
    return (
      <EmptyState
        title="No reviews waiting"
        description="A case reaches here when it moves into a stage your firm reviews. Approving or sending it back is done on the case."
      />
    )
  }

  return (
    <div className="space-y-4">
      <ListToolbar list={list} placeholder="Search by number or title" />
      <ListTable list={list} rowKey={(row) => row.id} rowHref={(row) => `/cases/${row.case_id}`} />
    </div>
  )
}

export function Cases() {
  const { can } = useEvaluation()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [vocab, setVocab] = useState<CaseVocabulary | null>(null)
  const [rows, setRows] = useState<Case[]>([])
  const [reviews, setReviews] = useState<ReviewRow[]>([])
  const [requests, setRequests] = useState<ServiceRequest[]>([])
  const [clientList, setClientList] = useState<ClientRecord[]>([])
  const [total, setTotal] = useState(0)
  const [q, setQ] = useState('')
  // Whether the queue holds more than one read returns — only then does the
  // search go to the server as well.
  const [truncated, setTruncated] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)

  const tabs = useMemo(() => visibleQueues(vocab), [vocab])
  const queue = (params.get('queue') as Queue) ?? 'mine'
  const canCreate = can(CASE, 'can_create')

  useEffect(() => {
    void cases
      .vocabulary()
      .then((body) => setVocab(body.data))
      .catch(() => setError('Your permissions could not be read.'))
    // The client names a row shows, and the picker accepting a request needs.
    // Best effort: a list without names is still a list.
    void clients
      .list({ limit: QUEUE_LIMIT })
      .then((body) => setClientList(body.data))
      .catch(() => undefined)
  }, [])

  const load = useCallback(async () => {
    setError(null)
    if (queue === 'reviews') {
      const body = await cases.reviews('open')
      setReviews(body.data)
      setTotal(body.data.length)
    } else if (queue === 'requests') {
      const inbox = await caseFlows.requests('new')
      setRequests(inbox.data)
      setTotal(Number(inbox.meta?.total ?? inbox.data.length))
    } else {
      const body = await cases.queue(queue, { q: q || undefined, limit: QUEUE_LIMIT })
      const count = Number(body.meta?.total ?? body.data.length)
      setRows(body.data)
      setTotal(count)
      if (!q) setTruncated(count > body.data.length)
    }
    setLoading(false)
  }, [queue, q])

  useEffect(() => {
    setLoading(true)
    void load().catch((err) => {
      setError(err instanceof ApiError ? err.message : 'Your cases could not be loaded.')
      setLoading(false)
    })
  }, [load])

  const onSearch = useCallback(
    (value: string) => {
      if (truncated || q) setQ(value)
    },
    [truncated, q],
  )

  const select = (next: string) => {
    const copy = new URLSearchParams(params)
    copy.set('queue', next)
    setParams(copy, { replace: true })
    setQ('')
    setTruncated(false)
  }

  const clientNames = useMemo(
    () => new Map(clientList.map((c) => [c.id, c.display_name || c.legal_name])),
    [clientList],
  )

  const counting = queue === 'reviews' || queue === 'requests'
  const description = loading
    ? 'The work your firm has open, and where each piece of it is.'
    : counting
      ? `${total} waiting on your firm.`
      : total > rows.length
        ? `${total} cases — you are seeing the first ${rows.length}; search to reach the rest.`
        : `${total} ${total === 1 ? 'case' : 'cases'} — the work your firm has open, and where each piece of it is.`

  return (
    <>
      <ListPage
        title="Cases"
        description={description}
        primary={
          canCreate ? (
            <Button onClick={() => setCreating(true)}>
              <Plus size={16} aria-hidden /> Open a case
            </Button>
          ) : undefined
        }
        loading={loading}
        error={error}
        // Never the page's own empty state: the queue tabs must stay, so each
        // queue says it is empty under them.
        isEmpty={false}
        empty={{ title: 'No cases', description: '' }}
      >
        <Tabs
          idBase="queues"
          active={queue}
          onChange={select}
          tabs={tabs.map((tab) => ({
            id: tab,
            label: QUEUE_LABELS[tab],
            badge:
              tab === queue && tab === 'reviews' && reviews.length > 0
                ? reviews.length
                : tab === queue && tab === 'requests' && requests.length > 0
                  ? requests.length
                  : undefined,
          }))}
        />
        <div id={`queues-${queue}-panel`} role="tabpanel" aria-labelledby={`queues-${queue}`}>
          {queue === 'reviews' ? (
            <ReviewList rows={reviews} />
          ) : queue === 'requests' ? (
            <RequestList
              rows={requests}
              // Only active clients take a new case, as before.
              clientList={clientList.filter((c) => c.status === 'active')}
              onDone={() => void load()}
            />
          ) : (
            <CaseList
              key={queue}
              queue={queue}
              rows={rows}
              clientNames={clientNames}
              canCreate={canCreate}
              onCreate={() => setCreating(true)}
              onSearch={onSearch}
            />
          )}
        </div>
      </ListPage>

      {creating && (
        <NewCaseDialog
          onClose={() => setCreating(false)}
          // **Go to the case, do not reload the queue.** A case opens with no
          // assignee (FR-E5: assignment is a manager's act), so on the default
          // *Mine* queue — which filters on assigned-or-reviewing — reloading
          // shows the person who just pressed the button exactly nothing.
          // Found by opening the app as a real user, 2026-09-10.
          onOpened={(caseId) => {
            setCreating(false)
            navigate(`/cases/${caseId}`)
          }}
        />
      )}
    </>
  )
}
